import React, { useEffect, useState } from "react";
import { getTicket } from "@network/Profile";
import moment from "moment";

import { TicketScreen, TicketType } from "@const/profile";

import { PaginateTicket } from "@network/Profile/type";
import { getTicketCounts } from "@network/Ticket";
import { useNavigate } from "react-router-dom";

interface StateStatus {
  Available: string;
  Used: string;
  // Registered: string;
  Expired: string;
}

const ProfileTicket: React.FC<StateStatus> = () => {
  const [tableData, setTableData] = useState<any>();
  const [allTickets, setAllTickets] = useState<any[]>([]); // Store all fetched tickets
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'questions' | 'others'>('questions');
  const [ticketCounts, setTicketCounts] = useState<any>({});
  const paginate: PaginateTicket = {
    limit: 100, // Fetch more tickets to filter client-side
    offset: 0,
  };
  const nav = useNavigate()

  // Filter tickets based on tab type
  const filterTicketsByTab = (tickets: any[], tabType: 'questions' | 'others') => {
    // if (tabType === 'questions') {
    //   // Show tickets from ticket generator (CLICK_WIDGET) or games (ANSWER_QUESTION)
    //   return tickets.filter((ticket: any) => {
    //     const source = ticket.source?.toUpperCase();
    //     return source === TicketSource.ANSWER_QUESTION;
    //   });
    // } else {
    //   // Show tickets from referral (INVITE_FRIEND, CLICK_INVITE_LINK) or profile creation (COMPLETE_PROFILE)
    //   return tickets.filter((ticket: any) => {
    //     const source = ticket.source?.toUpperCase();
    //     return source !== TicketSource.ANSWER_QUESTION;
    //   });
    // }
    return tickets
  };


  const fetchTicketCounts = async () => {
    try {
      // setLoading(true);
      const counts = await getTicketCounts();
      setTicketCounts(counts);
    } catch (error) {
      console.error('Error fetching ticket counts:', error);
    }
  };

  // Fetch all tickets when component mounts
  useEffect(() => {
    const fetchAllTickets = async () => {
      try {
        paginate.offset = 0;
        paginate.screen = TicketScreen.MY_TICKET_LIST;

        const response = await getTicket(paginate);
        const datarows = response.data.tickets;
        const resultData = datarows.map((obj: any) => {
          return obj; // Keep all fields including createdAt
        });

        setAllTickets(resultData);
      } catch (error) {
        console.error("Error fetching ticket data:", error);
      }
    };
    fetchAllTickets();
    fetchTicketCounts();
  }, []);

  // Filter and paginate tickets when tab or page changes
  useEffect(() => {
    if (allTickets.length > 0) {
      const filteredTickets = filterTicketsByTab(allTickets, activeTab);
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

      setTableData(paginatedTickets);
      setTotalPages(Math.ceil(filteredTickets.length / itemsPerPage));
    }
  }, [currentPage, activeTab, allTickets]);

  // const handlePageChange = (page: number) => {
  //   setCurrentPage(page);
  // };

  // const handleTabChange = (tab: 'questions' | 'others') => {
  //   setActiveTab(tab);
  //   setCurrentPage(1); // Reset to first page when changing tabs
  // };

  return (
    <>
      {/* Modern My Tickets Hero Section */}
      <section className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-10 sm:py-12 md:py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 lg:gap-12">

            {/* Left Content */}
            <div className="flex-1 text-white space-y-4 sm:space-y-5 md:space-y-6">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 sm:gap-3 bg-white/20 backdrop-blur-sm px-4 py-2 sm:px-5 sm:py-3 rounded-full">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                </svg>
                <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide">Your Tickets</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight">
                My Tickets
              </h1>

              {/* Subheading */}
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 max-w-2xl">
                Check all your tickets from here. View available, used, and expired tickets in one place.
              </p>

              {/* Ticket Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 pt-3 sm:pt-4">
                <div className="bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/20">
                  <p className="text-xs sm:text-sm text-white/70 mb-1">Available</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-black text-white">{ticketCounts.availableTickets || 0}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/20">
                  <p className="text-xs sm:text-sm text-white/70 mb-1">Used</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-black text-white">{ticketCounts.usedTickets || 0}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/20">
                  <p className="text-xs sm:text-sm text-white/70 mb-1">Expired</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-black text-white">{ticketCounts.expiredTickets || 0}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 pt-4 sm:pt-5 md:pt-6 border-t border-white/20">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Track All Tickets</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">View History</span>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Real-time Updates</span>
                </div>
              </div>
            </div>

            {/* Right Content - Ticket Visual */}
            <div className="flex-shrink-0 w-full max-w-sm lg:max-w-xs xl:max-w-md">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl sm:blur-3xl"></div>

                {/* Ticket Display */}
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 border border-white/20 shadow-2xl">
                  <div className="text-center space-y-4 sm:space-y-5 md:space-y-6">
                    <div className="text-6xl sm:text-7xl md:text-8xl">🎫</div>
                    <div className="text-white">
                      <p className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">Your Ticket Wallet</p>
                      <p className="text-xs sm:text-sm opacity-80">Manage all your raffle tickets in one place</p>
                    </div>
                    <div className="bg-white/20 rounded-lg sm:rounded-xl p-3 sm:p-4">
                      <p className="text-xs sm:text-sm text-white/70 mb-1">Total Tickets</p>
                      <p className="text-3xl sm:text-4xl font-black text-white">{ticketCounts.totalTickets || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 bg-green-400 text-green-900 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-bold text-xs sm:text-sm shadow-lg">
                  Active ✓
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 md:h-12" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#EAF0D5" />
          </svg>
        </div>
      </section>

      {/* Modern Tickets Display Section */}
      <div className="container mx-auto px-4 sm:px-5 md:px-6 lg:px-8 py-6 sm:py-7 md:py-8">

        {/* Header with Tabs */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 mb-4 sm:mb-5 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1f2c2b]">My Tickets</h2>
              <p className="text-xs sm:text-sm text-[#6a7b76] mt-1">Manage and track all your raffle tickets</p>
            </div>

            {/* Total Tickets Badge */}
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-lg sm:rounded-xl shadow-md">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
              </svg>
              <div>
                <p className="text-xs opacity-90">Total Tickets</p>
                <p className="text-lg sm:text-xl font-bold">{ticketCounts.totalTickets || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tickets Grid */}
        {/* Tickets Table */}
        {tableData?.length > 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-[#4B975B] to-[#5dad6d]">
                    <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                        </svg>
                        <span className="hidden sm:inline">Ticket ID</span>
                        <span className="sm:hidden">ID</span>
                      </div>
                    </th>
                    <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Status
                    </th>
                    <th className="hidden md:table-cell px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tableData.map((ticket: any, index: number) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      {/* Row Number */}
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#4B975B] to-[#5dad6d] rounded-full flex items-center justify-center">
                            <span className="text-xs sm:text-sm font-bold text-white">{index + 1}</span>
                          </div>
                        </div>
                      </td>

                      {/* Ticket ID */}
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 bg-[#4B975B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 sm:w-5.5 sm:h-5.5 md:w-6 md:h-6 text-[#4B975B]" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-bold text-[#1f2c2b] truncate">{ticket.id || 'N/A'}</p>
                            <p className="text-xs text-[#6a7b76] hidden sm:block">Ticket #{index + 1}</p>
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-xs sm:text-sm font-medium text-[#1f2c2b]">{ticket.type || 'N/A'}</span>

                          <span className="text-[10px] text-red-500 font-medium">
                            Expires: {moment(ticket.expiredDate).format('DD MMM, hh:mm A')}
                          </span>

                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-1 md:px-3 rounded-full text-xs font-bold ${ticket.status === 'AVAILABLE'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : ticket.status === 'USED' || ticket.status === 'REGISTERED'
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}>
                          <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mr-1.5 sm:mr-2 flex-shrink-0 ${ticket.status === 'AVAILABLE'
                            ? 'bg-green-500'
                            : ticket.status === 'USED' || ticket.status === 'REGISTERED'
                              ? 'bg-blue-500'
                              : 'bg-gray-500'
                            }`}></span>
                          {ticket.status === 'AVAILABLE' ? 'Available' : ticket.status === 'USED' || ticket.status === 'REGISTERED' ? 'Used' : 'Expired'}
                        </span>
                      </td>

                      {/* Value */}
                      <td className="hidden md:table-cell px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#4B975B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-bold text-[#4B975B]">1 Entry</span>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                        {ticket.status === 'AVAILABLE' ? (
                          <button
                            onClick={() => nav('/dashboard')}
                            className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-200 text-xs sm:text-sm"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                            <span className="hidden xs:inline">Play Now</span>
                            <span className="xs:hidden">Play</span>
                          </button>
                        ) : ticket.status === 'USED' || ticket.status === 'REGISTERED' ? (
                          <button
                            disabled
                            className="inline-flex items-center gap-1.5 sm:gap-2 bg-gray-300 text-gray-500 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-semibold cursor-not-allowed opacity-60 text-xs sm:text-sm"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Used
                          </button>
                        ) : (
                          <button
                            disabled
                            className="inline-flex items-center gap-1.5 sm:gap-2 bg-gray-300 text-gray-500 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-semibold cursor-not-allowed opacity-60 text-xs sm:text-sm"
                          >
                            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Expired
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Empty State
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 md:p-10 lg:p-12 text-center">
            <div className="max-w-md mx-auto space-y-3 sm:space-y-4">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1f2c2b]">No Tickets Found</h3>
              <p className="text-sm sm:text-base text-[#6a7b76]">
                You don't have any {activeTab === 'questions' ? 'question' : 'other'} tickets yet.
                Start earning tickets by completing tasks!
              </p>
              <button
                onClick={() => window.location.href = '/ticket-generator'}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl font-semibold hover:shadow-lg transition-all duration-200 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Earn Tickets Now
              </button>
            </div>
          </div>
        )}

        {/* Pagination (if needed) */}
        {tableData?.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-7 md:mt-8">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-white border-2 border-gray-200 text-[#6a7b76] font-semibold hover:border-[#4B975B] hover:text-[#4B975B] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>

            <div className="flex items-center gap-1.5 sm:gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg font-semibold transition-all text-xs sm:text-sm ${currentPage === pageNum
                      ? 'bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white shadow-lg'
                      : 'bg-white border-2 border-gray-200 text-[#6a7b76] hover:border-[#4B975B]'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-white border-2 border-gray-200 text-[#6a7b76] font-semibold hover:border-[#4B975B] hover:text-[#4B975B] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs sm:text-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileTicket;
